import React, { useState, useEffect, useContext } from 'react';
import { NavLink } from 'react-router-dom';
import * as LuIcons from 'react-icons/lu';
import SafeIcon from '../common/SafeIcon';
import { supabase } from '../lib/supabase';
import { TopBarSocial } from './SharedUI';
import { ThemeContext } from '../App';

const { LuCarFront, LuMenu, LuX, LuCalculator, LuUsers, LuVideo, LuMoon, LuSun } = LuIcons;

const DEFAULT_NAV = {
  navbar_site_name: 'كارد زاير',
  navbar_site_tagline: 'سيارات + جمركة',
  navbar_home_label: 'الرئيسية',
  navbar_bg_color: '#ffffff',
  navbar_accent_color: '#10b981',
  navbar_logo_url: '',
  navbar_logo_size: '40',
};

const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [nav, setNav] = useState(DEFAULT_NAV);
  const { isDark, toggleTheme } = useContext(ThemeContext);

  useEffect(() => {
    const handler = () => setScrolled(window.scrollY > 10);
    window.addEventListener('scroll', handler);
    return () => window.removeEventListener('scroll', handler);
  }, []);

  useEffect(() => {
    const fetchNav = async () => {
      try {
        const { data } = await supabase.from('site_settings').select('setting_key, setting_value');
        if (data) {
          const mapped = { ...DEFAULT_NAV };
          data.forEach(row => {
            if (row.setting_key in mapped) mapped[row.setting_key] = row.setting_value;
          });
          setNav(mapped);
        }
      } catch (err) {}
    };
    fetchNav();
  }, []);

  const LogoContent = () => {
    const logoSize = Number(nav.navbar_logo_size) || 40;
    if (nav.navbar_logo_url) {
      return (
        <img 
          src={nav.navbar_logo_url} 
          alt={nav.navbar_site_name} 
          style={{ height: `${logoSize}px`, width: 'auto', objectFit: 'contain' }} 
          onError={(e) => { e.target.style.display = 'none'; }} 
        />
      );
    }
    return (
      <div className="rounded-xl flex items-center justify-center transition-all" style={{ background: nav.navbar_accent_color, height: `${logoSize}px`, width: `${logoSize}px` }}>
        <SafeIcon icon={LuCarFront} className="text-white" style={{ width: `${logoSize * 0.6}px`, height: `${logoSize * 0.6}px` }} />
      </div>
    );
  };

  const navLinks = [
    { to: "/", icon: LuCarFront, label: nav.navbar_home_label },
    { to: "/calculator", icon: LuCalculator, label: "حاسبة الجمركة" },
    { to: "/reviews", icon: LuVideo, label: "آراء الزبائن" },
    { to: "/affiliate", icon: LuUsers, label: "التسويق بالعمولة" }
  ];

  return (
    <nav className={`sticky top-0 z-50 transition-shadow duration-300 dark:bg-slate-900 ${scrolled ? 'shadow-md' : ''}`} style={{ backgroundColor: isDark ? '#0f172a' : nav.navbar_bg_color, borderBottom: `1px solid ${nav.navbar_accent_color}20` }}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between h-16 items-center">
          <NavLink to="/" className="flex items-center gap-3">
            <LogoContent />
            {!nav.navbar_logo_url && (
              <div className="flex flex-col justify-center hidden sm:flex">
                <span className="font-black text-lg text-slate-800 dark:text-white leading-none mb-1">{nav.navbar_site_name}</span>
                <span className="text-xs font-bold leading-none" style={{ color: nav.navbar_accent_color }}>
                  {nav.navbar_site_tagline}
                </span>
              </div>
            )}
          </NavLink>

          {/* Desktop Menu */}
          <div className="hidden lg:flex items-center gap-2">
            {navLinks.map((link) => (
              <NavLink 
                key={link.to} 
                to={link.to} 
                className={({ isActive }) => `flex items-center px-4 py-2 rounded-xl text-sm font-bold transition-all ${isActive ? 'text-white shadow-sm' : 'text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800 hover:text-slate-900 dark:hover:text-white'}`}
                style={({ isActive }) => isActive ? { backgroundColor: nav.navbar_accent_color } : {}}
              >
                <SafeIcon icon={link.icon} className="h-4 w-4 me-1.5" />
                {link.label}
              </NavLink>
            ))}
          </div>

          <div className="hidden lg:flex items-center gap-4">
            <button onClick={toggleTheme} className="p-2 rounded-xl text-slate-500 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors">
              <SafeIcon icon={isDark ? LuSun : LuMoon} className="w-5 h-5" />
            </button>
            <TopBarSocial />
          </div>

          <div className="flex items-center gap-2 lg:hidden">
            <button onClick={toggleTheme} className="p-2 rounded-xl text-slate-500 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors">
              <SafeIcon icon={isDark ? LuSun : LuMoon} className="w-5 h-5" />
            </button>
            <button onClick={() => setMobileOpen(o => !o)} className="p-2 rounded-xl text-slate-500 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800">
              <SafeIcon icon={mobileOpen ? LuX : LuMenu} className="w-6 h-6" />
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileOpen && (
        <div className="lg:hidden border-t dark:border-slate-800 bg-white dark:bg-slate-900 px-4 py-3 space-y-2 shadow-inner">
          {navLinks.map((link) => (
            <NavLink 
              key={link.to} 
              to={link.to} 
              onClick={() => setMobileOpen(false)} 
              className={({ isActive }) => `flex items-center px-4 py-3 rounded-xl text-sm font-bold transition-colors ${isActive ? 'bg-slate-50 dark:bg-slate-800 text-emerald-600' : 'text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800'}`}
            >
              <SafeIcon icon={link.icon} className="h-5 w-5 me-2" />
              {link.label}
            </NavLink>
          ))}
          <div className="pt-3 mt-3 border-t border-slate-100 dark:border-slate-800 flex justify-center">
            <TopBarSocial />
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navbar;